<template lang="pug">
.v-field-auth-phone.field-auth-phone(v-if="field" dir="ltr")
	label.field-label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
		| {{ field.label }}
		span.field-text__star(v-if="field.required") *
	.field-auth-phone__controls
		.field-auth-phone__country(:class="{'field-auth-phone__country--open': open}")
			button.field-auth-phone__country-btn(
				type="button"
				:aria-label="field.countrySelectorLabel"
				:aria-expanded="open ? 'true' : 'false'"
				:disabled="field.disabled"
				@click="open = !open")
				span.field-auth-phone__flag(aria-hidden="true") {{ selectedOption.shortCode || selectedOption.flag }}
				span.field-auth-phone__code {{ selectedOption.code }}
				v-svg-icon.field-auth-phone__arrow(icon="chevron-down-sm")
			.field-auth-phone__menu.scrollbar(v-if="open" role="listbox")
				button.field-auth-phone__option(
					type="button"
					role="option"
					v-for="option in countries"
					:key="option.id"
					:aria-selected="option.id === selectedVariant ? 'true' : 'false'"
					:class="{'field-auth-phone__option--active': option.id === selectedVariant}"
					@click="selectCountry(option)")
					span.field-auth-phone__flag(aria-hidden="true") {{ option.shortCode || option.flag }}
					span.field-auth-phone__country-name {{ option.country }}
					span.field-auth-phone__option-code {{ option.code }}
		.field-auth-phone__number
			input.field-input(
				:key="selectedVariant"
				:id="uniqueId"
				type="tel"
				inputmode="tel"
				:disabled="field.disabled"
				:placeholder="activeMask.placeholder"
				:value="localValue"
				v-imask="computedMask"
				@accept="onAccept")
</template>

<script>
import uniqueId from '../scripts/helpers/uniqueId.js';

export default {
	props: {
		field: {
			type: Object,
			default: null,
			required: true
		},
		modelValue: {
			type: [String, Number],
			default: ''
		}
	},

	emits: ['update:model-value'],

	data() {
		return {
			selectedVariant: this.field.defaultCountry || 'cy-357',
			localValue: '',
			open: false,
			uniqueId: uniqueId('field-auth-phone_'),
			lastEmittedValue: null
		};
	},

	computed: {
		countries() {
			return Array.isArray(this.field.countries) ? this.field.countries : [];
		},

		selectedOption() {
			return this.countries.find(option => option.id === this.selectedVariant)
				|| this.countries[0]
				|| {flag: '', shortCode: '', code: '', dialingCode: '', masks: [{mask: '', placeholder: ''}]};
		},

		activeMask() {
			const masks = Array.isArray(this.selectedOption.masks) ? this.selectedOption.masks : [];
			const conditional = masks.find(mask => mask.startsWith && this.localValue.indexOf(mask.startsWith) === 0);
			return conditional || masks.find(mask => !mask.startsWith) || masks[0] || {mask: '', placeholder: ''};
		},

		computedMask() {
			return {
				mask: this.activeMask.mask || '',
				lazy: true
			};
		}
	},

	watch: {
		modelValue: {
			immediate: true,
			handler(value) {
				if (String(value || '') === String(this.lastEmittedValue || '')) {
					return;
				}
				this.restoreValue(value);
			}
		}
	},

	created() {
		document.addEventListener('click', this.handleClickOutside);
		document.addEventListener('keydown', this.handleEsc);
	},

	unmounted() {
		document.removeEventListener('click', this.handleClickOutside);
		document.removeEventListener('keydown', this.handleEsc);
	},

	methods: {
		restoreValue(value) {
			const digits = String(value || '').replace(/\D/g, '');
			if (!digits) {
				this.selectedVariant = this.field.defaultCountry || this.countries[0]?.id || '';
				this.localValue = '';
				return;
			}

			const resolved = this.countries.find(option => {
				if (digits.indexOf(option.dialingCode) !== 0) return false;
				const local = digits.slice(option.dialingCode.length);
				return option.masks.some(mask => {
					const lengthMatches = Number(mask.length) === local.length;
					const prefixMatches = !mask.startsWith || local.indexOf(mask.startsWith) === 0;
					return lengthMatches && prefixMatches;
				});
			});

			if (resolved) {
				this.selectedVariant = resolved.id;
				this.localValue = digits.slice(resolved.dialingCode.length);
			}
		},

		selectCountry(option) {
			this.selectedVariant = option.id;
			this.localValue = '';
			this.open = false;
			this.emitValue();
		},

		onAccept(event) {
			this.localValue = event.detail.unmaskedValue || '';
			this.emitValue();
		},

		emitValue() {
			const value = this.localValue ? `+${this.selectedOption.dialingCode}${this.localValue}` : '';
			this.lastEmittedValue = value;
			this.$emit('update:model-value', value);
		},

		handleClickOutside(event) {
			if (this.$el && !this.$el.contains(event.target)) {
				this.open = false;
			}
		},

		handleEsc(event) {
			if (event.key === 'Escape') {
				this.open = false;
			}
		}
	}
};
</script>
